import React, {Component, ReactNode} from 'react'
import FacilitiesRepository from '../facilities/FacilitiesRepository'
import ReservationsRepository, {FacilityReservationDetail, ReservationOptionDetail} from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {convertToRoomNumberForResident, NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {Nullable, Optional} from '../../app/TypeHelpers'
import FacilityForReservation, {OptionSetting} from '../facilities/FacilityForReservation'
import {PaymentItem, PaymentItemForReservation} from './ReserveFacilityModel'
import moment from 'moment'
import {convertToMomentOrError, isCancelLimitOver, judgeFacilityReserveEditAuthority} from './helpers/helpers'
import {PaymentItemForViewOvernight} from './element/PaymentItemForViewOvernight'
import {
    AVAILABLE_OR_NOT,
    FACILITY_RESERVATION_CLASSIFICATION,
    PAYMENT_TYPE,
    RESERVATION_ACCEPTABILITY_FOR_RESIDENT,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
} from '../../CodeMaster'
import {PaymentItemForViewOption} from './element/PaymentItemForViewOption'
import {PaymentItemForViewDuration} from './element/PaymentItemForViewDuration'
import {
    DisplayItemCostInformationContainer,
    DisplayItemCostInformationData,
    DisplayItemCostPaymentTypeAndSubtotalContainer,
    DisplayItemPaymentType,
    DisplayItemSubtotalCost,
    DisplayItemTotalCostContainer,
    DisplayItemTotalCostHeader,
    DisplayItemTotalCostValue,
} from './ResidentReserveFacilityFormComponents/RRFFReservePaymentView'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {HttpError} from '../../network/errors/HttpError'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {Messages} from '../../Messages'
import {RequestError} from '../../network/response/RequestError'
import ReservationStatusTag from './components/ReservationStatusTag'
import RRFFFacilityInformation from './ResidentReserveFacilityFormComponents/RRFFFacilityInformation'
import {PlainBackLink} from '../../styledComponents/Links'
import {BlockSpacer, BlockSpacerPcOnly, currentMedia} from '../../styledComponents/ResponsiveConvenience'
import {RowFlexContainer} from '../../styledComponents/FlexComponents'
import {StyledCancelButton, StyledPageTitle, StyledSecondaryButton, ViewOnlyModeMessage} from './components/CommonStyle'
import {PrimaryStyledButton} from '../../styledComponents/Button'
import ReservationDetailItem from './components/ReservationDetailItem'
import styled from 'styled-components'
import {Colors, FontSize} from '../../styledComponents/Constants'
import PreformattedDiv from '../../styledComponents/PreformattedDiv'
import {
    setResidentMyReservationsPageSearchDate,
} from './ResidentMyReservationsComponents/RMRReservationStatusesComponent'
import {
    clearResidentReserveTopPageSearchConditions,
} from './ResidentReserveFacilityTopComponents/RRFTFacilitySearchComponent'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {Location} from 'react-router-dom'

interface LocationState {
    previousPageUrl: string
    errorResponseBody: Nullable<ErrorResponseBody>
}

interface Props {
    condominiumCd: string
    facilityId: number
    reserveId: number
    facilitiesRepo: FacilitiesRepository
    reservationsRepo: ReservationsRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident

    location: Location<LocationState>
}

interface State {
    facilityData: Nullable<FacilityForReservation>
    reservationData: Nullable<FacilityReservationDetail>
    errorResponseBody: Nullable<ErrorResponseBody>
    isDisplayUpdateButton: boolean
    isDisplayConfirmButton: boolean
    isDisplayCancelButton: boolean
    viewOnlyMode: boolean
}

const DUMMY_DISPLAY_ORDER_OPTION: number = 99

class ResidentReserveFacilityDetailPage extends Component<Props & ResidentPagePropsSet, State> {

    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.state = {
            facilityData: null,
            reservationData: null,
            errorResponseBody: null,
            isDisplayUpdateButton: false,
            isDisplayConfirmButton: false,
            isDisplayCancelButton: false,
            viewOnlyMode: false,
        }

        this.handleOnClickCancelButton = this.handleOnClickCancelButton.bind(this)
        this.handleOnClickChangeButton = this.handleOnClickChangeButton.bind(this)
        this.handleOnClickConfirmedButton = this.handleOnClickConfirmedButton.bind(this)
    }

    handleOnClickCancelButton(): void {
        const {navigate, condominiumCd, facilityId, reserveId, location} = this.props
        const {facilityData, reservationData} = this.state
        const {previousPageUrl} = location.state

        navigate(
            `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/cancelConfirm`,
            {
                state: {
                    previousPageUrl: previousPageUrl,
                    facilityInfo: facilityData,
                    reserveInfo: reservationData,
                },
            },
        )
    }

    handleOnClickChangeButton(): void {
        const {navigate, condominiumCd, facilityId, reserveId, location} = this.props
        const {previousPageUrl} = location.state

        navigate(
            `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/update`,
            {state: {previousPageUrl: previousPageUrl}},
        )
    }

    async handleOnClickConfirmedButton(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        if (this.state.reservationData === null) {
            throw Error('this.state.reservationData is null')
        }

        const {condominiumCd, facilityId, reserveId, reservationsRepo, navigate} = this.props
        try {
            const returnValue = await NatAsyncCallFunction(reservationsRepo.confirmFacilityReservation.bind(
                reservationsRepo,
                condominiumCd,
                facilityId,
                reserveId,
                this.state.reservationData,
            ), navigate)
            if (returnValue === null) {
                return
            }

            setResidentMyReservationsPageSearchDate(this.state.reservationData.startDate)
            clearResidentReserveTopPageSearchConditions()

            navigate(
                `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/confirmationComplete`,
                {replace: true, state: {transitionInputPage: false}},
            )
        } catch (e) {
            const httpError: HttpError = e as HttpError
            this.setState({
                    errorResponseBody: {
                        ...httpError.responseBody,
                        requestError: {
                            ...httpError.responseBody.requestError,
                            title: Messages.EFACC0016.toString(),
                        },
                    },
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async componentDidMount(): Promise<void> {
        let viewMode: boolean = false
        try {
            viewMode = !judgeFacilityReserveEditAuthority()
        } catch (e) {
            this.props.navigate(`/resident/error`, {state: {message: Messages.ESYCC0025.toString()}})
            return
        }

        await this.props.eventEmitter.showLoading()
        const {condominiumCd, facilityId, reserveId, reservationsRepo, navigate} = this.props
        try {
            let facilityData: Nullable<FacilityForReservation>
            try {
                facilityData = await this.getFacilityForReservation()
            } catch (e) {
                return
            }

            const facilityReserve: Nullable<FacilityReservationDetail> = await NatAsyncCallFunction(
                this.props.reservationsRepo.getFacilityReservation.bind(
                    reservationsRepo,
                    condominiumCd,
                    facilityId,
                    reserveId,
                ), navigate)
            if (facilityReserve === null) {
                return
            }

            const currentDateTime: Nullable<moment.Moment> = await this.getCurrentDate()
            if (currentDateTime === null) {
                return
            }

            let errorResponseBody: Nullable<ErrorResponseBody> =
                this.buttonDisplayControl(facilityData, facilityReserve, viewMode, currentDateTime)

            if (this.props.location.state) {
                if (this.props.location.state.errorResponseBody !== undefined) {
                    errorResponseBody = this.props.location.state.errorResponseBody
                }
            }
            this.setState({
                facilityData: facilityData,
                reservationData: facilityReserve,
                errorResponseBody: errorResponseBody,
                viewOnlyMode: viewMode,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return
    }

    async getCurrentDate(): Promise<Nullable<moment.Moment>> {
        const {currentDateTimeRepo, navigate} = this.props
        let currentDate: Nullable<moment.Moment>
        const currentTimeResponse = await NatAsyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            navigate,
        )
        if (currentTimeResponse === null || currentTimeResponse.details === null) {
            return null
        }
        currentDate = moment(currentTimeResponse.details.currentDateTime)
        return currentDate
    }

    buttonDisplayControl(
        facilityData: Nullable<FacilityForReservation>,
        reservationData: FacilityReservationDetail,
        viewOnlyMode: boolean,
        currentDateTime: moment.Moment,
    ): Nullable<ErrorResponseBody> {
        if (facilityData === null) {
            this.setState({
                isDisplayUpdateButton: false,
                isDisplayConfirmButton: false,
                isDisplayCancelButton: false,
            })
            return new ErrorResponseBody(new RequestError('', '',
                Messages.IRESR0012.toString(), Messages.IRESR0012.errorLevel,
            ), [])
        }

        const {
            residentReserve, cancelAvailability,
            cancelReceptionLimitDate, cancelReceptionLimitTime,
        } = facilityData
        const {
            reserveType, reserveStatus, startDate, startTime, facilityReserveCategory,
        } = reservationData

        let updateButton: boolean = false
        let confirmButton: boolean = false
        let cancelButton: boolean = false
        let errorResponseBody: Nullable<ErrorResponseBody> = null

        if (RESERVATION_TYPE.FIRST_COME.value === reserveType || RESERVATION_TYPE.LOTTERY.value === reserveType) {
            if (RESERVATION_STATUS.BEFORE_LOTTERY.value === reserveStatus ||
                RESERVATION_STATUS.ELECTION.value === reserveStatus) {
                updateButton = true
                confirmButton = false
                cancelButton = true
            }
            if (RESERVATION_STATUS.WAITING_FOR_CONFIRMATION.value === reserveStatus) {
                updateButton = false
                confirmButton = true
                cancelButton = true
            }
            if (RESERVATION_STATUS.DIS_ELECTION.value === reserveStatus ||
                RESERVATION_STATUS.CANCEL.value === reserveStatus) {
                updateButton = false
                confirmButton = false
                cancelButton = false
            }
        }

        const startDateTimeMoment: moment.Moment = convertToMomentOrError(startDate, startTime)
        if (viewOnlyMode) {
            updateButton = false
            confirmButton = false
            cancelButton = false
        } else if (startDateTimeMoment.isSameOrBefore(currentDateTime, 'minute')) {
            updateButton = false
            cancelButton = false
        } else if (AVAILABLE_OR_NOT.UNAVAILABLE.value === cancelAvailability.value) {
            updateButton = false
            cancelButton = false
            errorResponseBody = new ErrorResponseBody(
                new RequestError('', '', Messages.IFACR0005.toString(), Messages.IFACR0005.errorLevel),
                [])
        } else if (isCancelLimitOver(
            startDateTimeMoment,
            currentDateTime,
            cancelAvailability,
            cancelReceptionLimitDate,
            cancelReceptionLimitTime,
        )) {
            updateButton = false
            cancelButton = false
            errorResponseBody = new ErrorResponseBody(
                new RequestError('', '', Messages.IFACR0006.toString(), Messages.IFACR0006.errorLevel),
                [])
        } else if (RESERVATION_ACCEPTABILITY_FOR_RESIDENT.AGENT.value === residentReserve.value) {
            updateButton = false
            errorResponseBody = new ErrorResponseBody(
                new RequestError('', '', Messages.IFACR0004.toString(), Messages.IFACR0004.errorLevel),
                [])
        } else if (FACILITY_RESERVATION_CLASSIFICATION.RESIDENT.value !== facilityReserveCategory) {
            updateButton = false
            errorResponseBody = new ErrorResponseBody(
                new RequestError('', '', Messages.IFACR0004.toString(), Messages.IFACR0004.errorLevel),
                [])
        }

        this.setState({
            isDisplayUpdateButton: updateButton,
            isDisplayConfirmButton: confirmButton,
            isDisplayCancelButton: cancelButton,
        })

        return errorResponseBody
    }

    async getFacilityForReservation(): Promise<Nullable<FacilityForReservation>> {
        const apiResponse = await NatAsyncCallFunction(
            this.props.facilitiesRepo.getFacilityForReservationIfExist.bind(
                this.props.facilitiesRepo,
                this.props.condominiumCd,
                this.props.facilityId,
            )
            , this.props.navigate,
        )
        if (apiResponse === null) {
            throw new Error('サーバエラー')
        }
        return apiResponse.details
    }

    render(): ReactNode {
        const {condominiumCd} = this.props
        const {facilityData, reservationData} = this.state

        if (!reservationData) {
            return null
        }

        let previousPageUrl: string = `/resident/condominiums/${condominiumCd}/reserve-facility-top`
        if (this.props.location.state) {
            if (this.props.location.state.previousPageUrl !== undefined &&
                this.props.location.state.previousPageUrl !== null) {
                previousPageUrl = this.props.location.state.previousPageUrl
            }
        }

        const facilityOptions = facilityData && facilityData.facilityOptions

        const {
            startDate, startTime, endDate, endTime, stayCount, reservationTitle,
            sharedUseRoomNumberList, options, paymentInformation, reserveStatus,
        } = reservationData

        const {items} = paymentInformation

        const startDateTimeMoment = convertToMomentOrError(startDate, startTime)
        const endDateTimeMoment = convertToMomentOrError(endDate, endTime)
        const startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        const endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')

        return (
            <>
                <PlainBackLink to={previousPageUrl}>戻る</PlainBackLink>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <ResponsiveRowFlexContainer>
                    <StyledPageTitle>予約詳細</StyledPageTitle>
                    <BlockSpacerPcOnly width={'30px'}/>
                    <ReservationStatusTag
                        reservationStatus={RESERVATION_STATUS.codeToEnumConstants(reserveStatus)}
                    />
                </ResponsiveRowFlexContainer>
                {
                    this.state.viewOnlyMode && <>
                        <BlockSpacer height={'15px'}/>
                        <ViewOnlyModeMessage>{Messages.IFACR0003.toString()}</ViewOnlyModeMessage>
                    </>
                }
                {
                    (this.state.errorResponseBody !== null) &&
                    <ErrorMessage>
                        <BlockSpacer height={'15px'}/>
                        {
                            (this.state.errorResponseBody.requestError.title !== null) &&
                            <div>
                                {this.state.errorResponseBody.requestError.title}
                            </div>
                        }
                        <div>
                            {this.state.errorResponseBody.requestError.message}
                        </div>
                    </ErrorMessage>
                }

                <BlockSpacer height={'15px'}/>
                {
                    facilityData ?
                        <ReservationDetailItem
                            type={'component'} title={'施設名'}
                            collapse={true}
                            component={<RRFFFacilityInformation
                                openFacilityIntroductory={false}
                                {...facilityData}
                            />}
                            collapsedText={reservationData.facilityName as string}
                        />
                        :
                        <ReservationDetailItem
                            type={'texts'} title={'施設名'}
                            texts={[reservationData?.facilityName]}
                        />
                }
                <BlockSpacer height={'5px'}/>
                <ReservationDetailItem
                    type={'texts'}
                    title={'利用日時'}
                    texts={[
                        `${startDateTimeString}から`,
                        `${endDateTimeString}まで`,
                    ]}
                />

                {
                    (stayCount !== null) &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'} title={'宿泊数'}
                            texts={[`${stayCount}泊`]}
                        />
                    </>
                }

                {
                    (reservationTitle !== '') &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'} title={'予約タイトル'}
                            texts={[reservationTitle]}
                        />
                    </>
                }
                {
                    (sharedUseRoomNumberList.length > 0) &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'} title={'同時入室番号'}
                            texts={sharedUseRoomNumberList.map((user) =>
                                `${user.ridgeName}  ${convertToRoomNumberForResident(user.roomNumber)}`,
                            )}
                        />
                    </>
                }

                <BlockSpacer height={'5px'}/>
                <ReservationDetailItem
                    type={'component'} title={'お支払い金額'}
                    component={this.renderPaymentItem(
                        items,
                        paymentInformation.totalCharge as number,
                        startDateTimeMoment,
                        endDateTimeMoment,
                        stayCount,
                        facilityOptions,
                        options,
                    )}
                />

                {
                    (this.state.isDisplayUpdateButton) &&
                    <>
                        <BlockSpacer height={'30px'}/>
                        <StyledSecondaryButton onClick={this.handleOnClickChangeButton}>変更する</StyledSecondaryButton>
                    </>
                }
                {
                    (this.state.isDisplayConfirmButton) &&
                    <>
                        <BlockSpacer height={'30px'}/>
                        <PrimaryStyledButton onClick={this.handleOnClickConfirmedButton}>確定する</PrimaryStyledButton>
                    </>
                }
                {
                    (this.state.isDisplayCancelButton) &&
                    <>
                        <BlockSpacer height={'30px'}/>
                        <StyledCancelButton onClick={this.handleOnClickCancelButton}>キャンセルする</StyledCancelButton>
                    </>

                }
                <BlockSpacer height={'15px'}/>
            </>
        )
    }

    renderPaymentItem(
        paymentItemList: PaymentItemForReservation[],
        totalCharge: number,
        startDateTimeMoment: moment.Moment,
        endDateTimeMoment: moment.Moment,
        stayCount: Nullable<number>,
        facilityOptions: Nullable<OptionSetting[]>,
        reserveOptions: ReservationOptionDetail[],
    ): ReactNode {
        const paymentItems = convertToPaymentItems(
            paymentItemList,
            stayCount,
            startDateTimeMoment,
            endDateTimeMoment,
            facilityOptions,
            reserveOptions,
        )
        return <>
            <BlockSpacer height={'10px'}/>
            {
                paymentItems.map((item, index) => {
                    return (<DisplayItemCostInformationContainer key={index}>
                        <DisplayItemCostInformationData>
                            {item.article}：{item.quantityWithUnit}
                        </DisplayItemCostInformationData>
                        <DisplayItemCostPaymentTypeAndSubtotalContainer>
                            <DisplayItemPaymentType>
                                {item.paymentType.label}
                            </DisplayItemPaymentType>
                            <DisplayItemSubtotalCost>
                                {
                                    (item.charge !== null)
                                        ? formatNumberWithCommas(item.charge)
                                        : '-'
                                }
                                円
                            </DisplayItemSubtotalCost>
                        </DisplayItemCostPaymentTypeAndSubtotalContainer>
                    </DisplayItemCostInformationContainer>)
                })
            }
            <BlockSpacer height={'20px'}/>
            <DisplayItemTotalCostContainer>
                <DisplayItemTotalCostHeader>合計</DisplayItemTotalCostHeader>
                <DisplayItemTotalCostValue>{formatNumberWithCommas(totalCharge)}円</DisplayItemTotalCostValue>
            </DisplayItemTotalCostContainer>
        </>
    }
}

const convertToPaymentItems = (
    paymentItemList: PaymentItemForReservation[],
    stayCount: Nullable<number>,
    startDateTimeMoment: moment.Moment,
    endDateTimeMoment: moment.Moment,
    facilityOptions: Nullable<OptionSetting[]>,
    reserveOptions: ReservationOptionDetail[],
): PaymentItem[] => {
    return paymentItemList.map((item) => {
        if (item.paymentType === null) {
            throw Error('item.paymentType cannot be null')
        }
        if (item.charge === null) {
            throw Error('item charge cannot be null')
        }

        const paymentType = PAYMENT_TYPE.codeToEnumConstants(item.paymentType)
        const isFacilityPaymentItem = (item.id === null)
        if (isFacilityPaymentItem) {
            if (stayCount !== null) {
                return new PaymentItemForViewOvernight(paymentType, item.charge, stayCount)
            } else {
                return new PaymentItemForViewDuration(paymentType, item.charge, startDateTimeMoment, endDateTimeMoment)
            }
        } else {
            let facilityOption: Optional<OptionSetting>
            if (facilityOptions !== null) {
                facilityOption = findFacilityOption(item.id, facilityOptions)
            }
            const reserveOption = findReservationOption(item.id, reserveOptions)

            return new PaymentItemForViewOption(
                reserveOption.optionId,
                reserveOption.name,
                PAYMENT_TYPE.codeToEnumConstants(reserveOption.paymentMethod),
                item.charge,
                reserveOption.quantity,
                reserveOption.unitName,
                facilityOption ? facilityOption.sortOrder : DUMMY_DISPLAY_ORDER_OPTION,
            )
        }
    }).map(item => item.toObject())
}

const findFacilityOption = (optionId: Nullable<number>, facilityOptions: OptionSetting[]): Optional<OptionSetting> => {
    if (optionId === null) {
        throw Error('optionId cannot be null')
    }
    return facilityOptions.find(option => option.optionId === optionId)
}

const findReservationOption = (
    optionId: Nullable<number>,
    reserveOptions: ReservationOptionDetail[],
): ReservationOptionDetail => {
    if (optionId === null) {
        throw Error('optionId cannot be null')
    }
    const foundItems: ReservationOptionDetail[] = reserveOptions.filter(option => option.optionId === optionId)
    if (foundItems.length === 0) {
        throw Error('FacilityReserveOption is not found')
    }
    return foundItems[0]
}

export default createResidentPage(ResidentReserveFacilityDetailPage)

const ErrorMessage = styled(PreformattedDiv)`
  color: ${Colors.Alert};
  font-size: ${FontSize.Body};
`

const ResponsiveRowFlexContainer = styled(RowFlexContainer)`
  ${currentMedia.greaterThan('pc')`
  justify-content: flex-start;
  `}
`
